import { useEffect } from 'react';

// Arabic text in Amiri and Persian text in B Nazanin - wherever it appears, in whatever language the app is in.
//
// The theme puts a language's face first for the app's own language (theme/theme.js fontStack): an Arabic UI is Amiri, a Farsi
// UI is B Nazanin. In English the stack never named either, so every Arabic or Persian name, address and note in an English
// screen fell back to a system font, and a Persian name in the Arabic UI came out in Amiri.
//
// So the TEXT is told apart instead of the screen: every element whose own text is Arabic-script gets `data-sf="ar"` or
// `data-sf="fa"`, and index.scss gives those the matching face (with !important - MUI sets a font on almost everything).
//   · Arabic and Persian share most letters, so a text is classified by the letters only one of them uses (classifyScript).
//     A word with none of those ("محمد", "اصفهان") follows the app's language, else Arabic.
//   · a text already in the app's own language is left alone - the theme covers it, so the Arabic and Farsi UIs tag only what
//     is of the OTHER script.
//   · text fields are classified by what is typed in them.
// One MutationObserver (and one set of input listeners) for the whole app, mounted once in App.js; changing the language
// re-classifies everything. The same rule runs on the server for printed documents (api/utils/scriptFonts.js): keep the two
// in step.

const SCRIPT = '\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF\\uFB50-\\uFDFF\\uFE70-\\uFEFF';
const HAS_SCRIPT = new RegExp(`[${SCRIPT}]`);
const FARSI_STRONG = /[پچژگ]/;                     // پ چ ژ گ - Arabic never uses them
const FARSI_WEAK = /[کی۰-۹ۀ]/g;               // ک ی and the Persian digits - Urdu and Kurdish use them too
const ARABIC_ONLY = /[كيةىأإؤئ٠-٩]/g;   // ك ي ة ى أ إ ؤ ئ and the Arabic-Indic digits

/** 'fa' | 'ar' | 'any' (Arabic script, but nothing that says which) | '' (no Arabic script at all). */
export function classifyScript(text) {
  const s = String(text == null ? '' : text);
  if (!HAS_SCRIPT.test(s)) return '';
  if (FARSI_STRONG.test(s)) return 'fa';
  const farsi = (s.match(FARSI_WEAK) || []).length;
  const arabic = (s.match(ARABIC_ONLY) || []).length;
  if (!farsi && !arabic) return 'any';
  return farsi >= arabic ? 'fa' : 'ar';
}

const ATTR = 'data-sf';
const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'SVG', 'PATH']);

const uiLang = () => {
  const l = String(document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
  return l === 'ar' || l === 'fa' ? l : '';
};

// the face an element's text wants, or '' when the app's own face already covers it
function wanted(text) {
  const found = classifyScript(text);
  if (!found) return '';
  const script = found === 'any' ? (uiLang() === 'fa' ? 'fa' : 'ar') : found;
  return script === uiLang() ? '' : script;
}

function apply(el, script) {
  if (script) {
    if (el.getAttribute(ATTR) !== script) el.setAttribute(ATTR, script);
  } else if (el.hasAttribute(ATTR)) {
    el.removeAttribute(ATTR);
  }
}

// an element's OWN text (not its children's): a card is not re-faced because one of its lines is
function tagFromText(el) {
  if (!el || el.nodeType !== 1 || SKIP.has(el.tagName.toUpperCase())) return;
  let own = '';
  for (let n = el.firstChild; n; n = n.nextSibling) if (n.nodeType === 3) own += n.nodeValue;
  apply(el, wanted(own));
}

const isField = (el) => el && el.nodeType === 1 && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA');
function tagField(el) {
  if (!isField(el)) return;
  if (el.tagName === 'INPUT' && /^(password|checkbox|radio|file|range|color|date|time|datetime-local|number|hidden)$/i.test(el.type)) return;
  apply(el, wanted(el.value));
}

function scan(root) {
  if (!root) return;
  if (root.nodeType === 3) { tagFromText(root.parentElement); return; }
  if (root.nodeType !== 1) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const seen = new Set();
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const p = n.parentElement;
    if (p && !seen.has(p) && HAS_SCRIPT.test(n.nodeValue)) { seen.add(p); tagFromText(p); }
  }
  if (isField(root)) tagField(root);
  if (root.querySelectorAll) root.querySelectorAll('input, textarea').forEach(tagField);
}

function rescanAll() {
  document.querySelectorAll(`[${ATTR}]`).forEach((el) => el.removeAttribute(ATTR));
  scan(document.body);
}

let started = 0;
let observer = null;
let stopListeners = null;

function start() {
  started += 1;
  if (started > 1) return;
  const subtrees = new Set();
  const texts = new Set();
  const fields = new Set();
  let timer = null;
  const flush = () => {
    timer = null;
    const s = [...subtrees]; const t = [...texts]; const f = [...fields];
    subtrees.clear(); texts.clear(); fields.clear();
    s.forEach(scan);
    t.forEach(tagFromText);
    f.forEach(tagField);
  };
  const schedule = () => { if (timer === null) timer = setTimeout(flush, 24); };

  observer = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === 'childList') {
        r.addedNodes.forEach((n) => subtrees.add(n));
        if (r.target.nodeType === 1) texts.add(r.target);          // its own text may have changed with its children
      } else if (r.type === 'characterData') {
        if (r.target.parentElement) texts.add(r.target.parentElement);
      } else if (r.type === 'attributes') {
        fields.add(r.target);                                       // a field's value attribute
      }
    }
    schedule();
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['value'] });
  // the language lives on <html lang>
  const langObserver = new MutationObserver(() => rescanAll());
  langObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });

  // typing changes a field's value without touching the DOM
  const onField = (e) => { if (isField(e.target)) tagField(e.target); };
  document.addEventListener('input', onField, true);
  document.addEventListener('change', onField, true);
  document.addEventListener('focusin', onField, true);

  stopListeners = () => {
    observer.disconnect(); langObserver.disconnect();
    document.removeEventListener('input', onField, true);
    document.removeEventListener('change', onField, true);
    document.removeEventListener('focusin', onField, true);
    if (timer !== null) clearTimeout(timer);
  };
  scan(document.body);
}

function stop() {
  started = Math.max(0, started - 1);
  if (started === 0 && stopListeners) { stopListeners(); stopListeners = null; observer = null; }
}

/** Mounted once in App.js. */
export default function useScriptFonts() {
  useEffect(() => {
    start();
    return stop;
  }, []);
}
